"use client";

import { ReactNode, Suspense } from "react";
import dynamic from "next/dynamic";
import { Toaster } from "react-hot-toast";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "@npm_leadtech/qr-ui-components";

import { StoreUtms } from "@/components/StoreUtms/StoreUtms";
import { StorePsp } from "@/components/StorePsp";
import { HistoryEvent } from "@/components/HistoryEvent";

import { LayoutProvider } from "@/contexts/LayoutContext";
import { UserProvider } from "@/contexts/UserContext";
import { GlobalProvider } from "@/contexts/GlobalContext/GlobalContext";
import { QREditorProvider } from "@/contexts/QREditorContext";
import { AmplitudeContextProvider } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { DatalayerProvider } from "@/contexts/DataLayerEventContext";
import { ABTestingProvider } from "@/contexts/ABTestingContext/ABTestingContext";
import { ThirdPartyScripts } from "./ThirdPartyScripts";

const DynamicLoadingBarProvider = dynamic(() =>
  import("@/contexts/LoadingBarContext/LoadingBarContext").then(
    ({ LoadingBarProvider }) => LoadingBarProvider
  )
);

const DynamicModalProvider = dynamic(() =>
  import("@/contexts/ModalContext").then(({ ModalProvider }) => ModalProvider)
);

import { NAMESPACES } from "@/internals/constants/namespaces";
import { usePrefetchRouter } from "@/hooks/navigation/usePrefetchRouter";
import { TranslationsProvider } from "@/hocs/TranslationsProvider";
import { getSiteDomain } from "@/utils/site";
import { getGTMId } from "@/utils/gtm";
import { THEME } from "@/themes/types";

const currentTheme: THEME = process.env.NEXT_PUBLIC_THEME as THEME;

export const LayoutWrapper = ({
  children,
  lang,
  genericErrors,
  modalData,
  resources,
}: {
  children: ReactNode;
  lang?: string;
  genericErrors: any;
  modalData?: any;
  resources?: any;
}) => {
  usePrefetchRouter([]);

  const siteDomain = getSiteDomain();
  const gtmId = getGTMId(siteDomain);

  return (
    <>
      <HistoryEvent />
      <ThirdPartyScripts gtmId={gtmId} />
      <ABTestingProvider>
        <TranslationsProvider
          locale={lang || "en-US"}
          resources={resources}
          namespaces={[NAMESPACES.GENERIC_ERRORS]}
        >
          <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ""}>
            <GlobalProvider>
              <AmplitudeContextProvider>
                <ThemeProvider theme={currentTheme}>
                  <LayoutProvider
                    modalData={modalData}
                    genericErrors={genericErrors}
                  >
                    <UserProvider>
                      <DatalayerProvider>
                        <QREditorProvider>
                          <DynamicLoadingBarProvider>
                            <DynamicModalProvider>
                              <Suspense>
                                <StoreUtms />
                                <StorePsp />
                              </Suspense>
                              {children}
                            </DynamicModalProvider>
                          </DynamicLoadingBarProvider>
                        </QREditorProvider>
                      </DatalayerProvider>
                    </UserProvider>
                  </LayoutProvider>
                </ThemeProvider>
              </AmplitudeContextProvider>
            </GlobalProvider>
          </GoogleOAuthProvider>
          <Toaster />
        </TranslationsProvider>
      </ABTestingProvider>
    </>
  );
};
